import React, { useState, useEffect } from 'react';
import Hero from './components/Hero';
import AnalysisResults from './components/AnalysisResults';

import { 
  analyzeDish, 
  findNearbyLocations,
  getCoordinatesFromCity,
  generateDishImage 
} from './services/geminiService';

import { handleRedirect } from './services/redirectService';
import { DishAnalysis, LocationResult, Coordinates, SavedMeal } from './types';

function App() {
  const [activeTab, setActiveTab] = useState<'analyze' | 'journal'>('analyze');
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<DishAnalysis | null>(null);
  
  const [loadingMaps, setLoadingMaps] = useState(false);
  const [restaurantResult, setRestaurantResult] = useState<LocationResult | null>(null);
  const [supermarketResult, setSupermarketResult] = useState<LocationResult | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  const [darkMode, setDarkMode] = useState(false);
  const [savedMeals, setSavedMeals] = useState<SavedMeal[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const wasRedirected = handleRedirect();
    if (wasRedirected) return; 

    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    requestLocation();
    const stored = localStorage.getItem('nutrimap_saved_meals');
    if (stored) {
      try {
        setSavedMeals(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse saved meals", e);
      }
    }
  }, []);

  const requestLocation = () => {
    if (!("geolocation" in navigator)) return;
    setIsLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setIsLocationLoading(false);
      },
      () => setIsLocationLoading(false)
    );
  };

  const handleCitySubmit = async (city: string) => {
    setIsLocationLoading(true);
    try {
      const coords = await getCoordinatesFromCity(city);
      setUserLocation(coords);
    } catch (error) {
      setGeneralError("Could not find city.");
    } finally {
      setIsLocationLoading(false);
    }
  };

  const handleAnalyze = async (input: File | string) => {
    setIsAnalyzing(true);
    setGeneralError(null);
    setAnalysisResult(null);
    setRestaurantResult(null);
    setSupermarketResult(null);
    setGeneratedImage(null);

    try {
      if (typeof input === 'string') throw new Error("Please upload an image.");
      
      const analysis = await analyzeDish(input);
      setAnalysisResult(analysis);
      setIsAnalyzing(false);

      if (userLocation) {
        setLoadingMaps(true);
        const [restaurants, supermarkets] = await Promise.all([
          findNearbyLocations(analysis.dishName, userLocation, 'restaurant'),
          findNearbyLocations("healthy grocery", userLocation, 'supermarket')
        ]);
        setRestaurantResult(restaurants);
        setSupermarketResult(supermarkets);
        setLoadingMaps(false);
      }
    } catch (error: any) {
      setGeneralError(error.message || "Analysis failed.");
      setIsAnalyzing(false);
      setLoadingMaps(false);
    }
  };

  const handleSaveMeal = async () => {
    if (!analysisResult) return;
    setIsSaving(true);
    
    let finalImage = generatedImage;
    if (!finalImage) {
      finalImage = await handleGenerateImage() || null;
    }
    
    const newSavedMeal: SavedMeal = {
      id: Date.now().toString(),
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      analysis: analysisResult,
      generatedImage: finalImage,
      restaurantResult: restaurantResult,
      supermarketResult: supermarketResult,
    };

    const updated = [newSavedMeal, ...savedMeals];
    setSavedMeals(updated);
    localStorage.setItem('nutrimap_saved_meals', JSON.stringify(updated));
    setIsSaving(false);
  };

  const handleDeleteSavedMeal = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = savedMeals.filter(m => m.id !== id);
    setSavedMeals(updated);
    localStorage.setItem('nutrimap_saved_meals', JSON.stringify(updated));
  };

  const handleGenerateImage = async () => {
    if (!analysisResult) return;
    setIsGeneratingImage(true);
    try {
      const imageUrl = await generateDishImage(analysisResult.dishName, analysisResult.description);
      setGeneratedImage(imageUrl);
      return imageUrl;
    } finally {
      setIsGeneratingImage(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 transition-colors duration-300">
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 h-16 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveTab('analyze')}>
            <div className="bg-indigo-600 rounded-lg p-1.5 shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <span className="font-bold text-xl">NutriMap</span>
          </div>
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            <button onClick={() => setActiveTab('analyze')} className={`px-4 py-2 rounded-lg text-sm font-bold ${activeTab === 'analyze' ? 'bg-white dark:bg-slate-700 text-indigo-600' : 'text-slate-500'}`}>Analyze</button>
            <button onClick={() => setActiveTab('journal')} className={`px-4 py-2 rounded-lg text-sm font-bold ${activeTab === 'journal' ? 'bg-white dark:bg-slate-700 text-indigo-600' : 'text-slate-500'}`}>Journal ({savedMeals.length})</button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'analyze' ? (
          <>
            <Hero onAnalyze={handleAnalyze} onCitySubmit={handleCitySubmit} isAnalyzing={isAnalyzing} isDarkMode={darkMode} toggleDarkMode={() => setDarkMode(!darkMode)} onRequestLocation={requestLocation} userLocation={userLocation} isLocationLoading={isLocationLoading} />
            {generalError && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-2xl mb-6">{generalError}</div>}
            {analysisResult && (
              <div className="relative">
                <div className="flex justify-end mb-6 sticky top-20 z-40">
                  <button onClick={handleSaveMeal} disabled={isSaving} className="bg-emerald-600 text-white px-6 py-3 rounded-full font-bold shadow-xl flex items-center gap-2 hover:bg-emerald-700">
                    {isSaving ? "Saving..." : "Save to Journal"}
                  </button>
                </div>
                <AnalysisResults analysis={analysisResult} restaurantResult={restaurantResult} supermarketResult={supermarketResult} loadingMaps={loadingMaps} onGenerateImage={handleGenerateImage} isGeneratingImage={isGeneratingImage} generatedImage={generatedImage} />
              </div>
            )}
          </>
        ) : (
          <div className="space-y-8">
            <header className="text-center py-10">
              <h1 className="text-4xl font-black mb-2">My Nutrition Journal</h1>
              <p className="text-slate-500">A history of your analyzed meals and health goals.</p>
            </header>

            {savedMeals.length === 0 ? (
              <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-[2rem]">
                <p className="text-slate-400 mb-4">No meals saved yet.</p>
                <button onClick={() => setActiveTab('analyze')} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-bold">Start First Analysis</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedMeals.map((meal) => (
                  <div key={meal.id} className="bg-white dark:bg-slate-800 rounded-[2rem] overflow-hidden border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
                    <div className="h-48 bg-slate-200 relative">
                      {meal.generatedImage ? (
                        <img src={meal.generatedImage} alt={meal.analysis.dishName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">No Image Available</div>
                      )}
                      <button onClick={(e) => handleDeleteSavedMeal(meal.id, e)} className="absolute top-3 right-3 bg-white/80 dark:bg-slate-900/80 p-2 rounded-full text-rose-500 hover:bg-rose-500 hover:text-white transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                    <div className="p-6">
                      <div className="text-[10px] font-bold text-indigo-500 uppercase mb-1">{meal.date}</div>
                      <h3 className="text-xl font-black mb-2 line-clamp-1">{meal.analysis.dishName}</h3>
                      <div className="flex gap-4 border-t border-slate-100 dark:border-slate-700 pt-4">
                        <div>
                          <p className="text-[9px] text-slate-400 uppercase font-bold">Calories</p>
                          <p className="font-bold">{meal.analysis.nutritionalInfo?.calories ?? 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-slate-400 uppercase font-bold">Protein</p>
                          <p className="font-bold">{meal.analysis.nutritionalInfo?.protein ?? 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;